import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { OrgsPanel } from "@/components/OrgsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "saathi_admin_token";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-CA", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function waLink(number) {
  const digits = (number || "").replace(/[^\d+]/g, "");
  return `https://wa.me/${digits.replace(/^\+/, "")}`;
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState("");

  const [rows, setRows] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // followup
  const [conv, setConv] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | pending | contacted
  const [tab, setTab] = useState("followups"); // followups | feedback

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [list, s, fb] = await Promise.all([
        axios.get(`${API}/admin/followups`, { params: { token: t } }),
        axios.get(`${API}/admin/stats`, { params: { token: t } }),
        axios.get(`${API}/admin/feedback`, { params: { token: t } }),
      ]);
      setRows(list.data.followups || []);
      setStats(s.data);
      setFeedback(fb.data.feedback || []);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError("That token didn't work. Try again.");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setAuthError("Couldn't reach the server. Try again.");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const doLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!tokenInput.trim()) return;
    const t = tokenInput.trim();
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRows([]);
    setFeedback([]);
    setStats(null);
    setTokenInput("");
  };

  const toggleContacted = async (row) => {
    try {
      const res = await axios.patch(
        `${API}/admin/followups/${row.id}`,
        { contacted: !row.contacted },
        { params: { token } }
      );
      setRows((rs) => rs.map((r) => (r.id === row.id ? res.data : r)));
      load(token);
    } catch {
      // silent
    }
  };

  const saveNotes = async (row, notes) => {
    try {
      const res = await axios.patch(
        `${API}/admin/followups/${row.id}`,
        { notes },
        { params: { token } }
      );
      setRows((rs) => rs.map((r) => (r.id === row.id ? res.data : r)));
    } catch {
      // silent
    }
  };

  const openConv = async (row) => {
    setSelected(row);
    const convId = row.conversation_id;
    if (!convId) {
      setConv({ messages: [] });
      return;
    }
    setConvLoading(true);
    setConv(null);
    try {
      const res = await axios.get(
        `${API}/admin/conversations/${convId}`,
        { params: { token } }
      );
      setConv(res.data);
    } catch (e) {
      if (e?.response?.status === 404) setConv({ messages: [] });
      else setConv({ error: "Could not load conversation" });
    }
    setConvLoading(false);
  };

  const markFeedbackRead = async (conversationId, read) => {
    try {
      await axios.patch(
        `${API}/admin/feedback/${conversationId}`,
        null,
        { params: { token, read } }
      );
      setFeedback((fs) => fs.map((f) => f.conversation_id === conversationId ? { ...f, read } : f));
      load(token);
    } catch { /* silent */ }
  };

  const closeDrawer = () => { setSelected(null); setConv(null); };

  const filtered = rows.filter((r) => {
    if (filter === "pending") return !r.contacted;
    if (filter === "contacted") return !!r.contacted;
    return true;
  });

  // ═══════════════════════ LOGIN ═══════════════════════
  if (!authed) {
    return (
      <div className="saathi-page" data-testid="admin-login">
        <div className="saathi-texture" />
        <div className="relative z-10 max-w-md mx-auto px-7 sm:px-10 min-h-screen flex flex-col justify-center">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-2">
            Hikari Labs · Admin
          </div>
          <h1 className="font-heading text-[48px] leading-[1] tracking-tight text-[#1A1A2E] mb-3">
            Saathi<br />follow-ups.
          </h1>
          <p className="text-[15px] text-[#8B7355] leading-relaxed mb-8 max-w-[300px]">
            Enter your team token to see everyone who asked for a WhatsApp follow-up.
          </p>
          <form onSubmit={doLogin} className="flex flex-col gap-3">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="admin token"
              className="w-full bg-white border border-[#EDE0D4] rounded-2xl px-5 py-4 text-[15px] text-[#1A1A2E] placeholder:text-[#8B7355]/70 focus:outline-none focus:border-[#E07B39] focus:ring-2 focus:ring-[#E07B39]/20 font-mono"
              autoFocus
              data-testid="admin-token-input"
            />
            {authError && (
              <div className="text-[13px] text-[#D32F2F]" data-testid="admin-auth-error">{authError}</div>
            )}
            <button
              type="submit"
              className="w-full bg-[#E07B39] hover:bg-[#c96a2e] text-white font-medium text-[15px] rounded-full py-4 min-h-[48px] transition-all active:scale-[0.98]"
              data-testid="admin-login-btn"
            >
              Sign in →
            </button>
          </form>
          <a href="/" className="block mt-6 text-[13px] text-[#8B7355] hover:text-[#1A1A2E] transition-colors font-mono">
            ← back to saathi
          </a>
        </div>
      </div>
    );
  }

  // ═══════════════════════ DASHBOARD ═══════════════════════
  return (
    <div className="saathi-page" data-testid="admin-dashboard">
      <div className="saathi-texture" />
      <div className="relative z-10 max-w-[1100px] mx-auto px-6 sm:px-10 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#E07B39] mb-2">
              Hikari Labs · Saathi admin
            </div>
            <h1 className="font-heading text-[44px] leading-[1] tracking-tight text-[#1A1A2E]">
              {tab === "orgs" ? "Organizations." : tab === "feedback" ? "Feedback." : "Follow-ups."}
            </h1>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => load(token)}
              className="text-[13px] font-mono text-[#8B7355] hover:text-[#1A1A2E] px-3 py-2 rounded-full border border-[#EDE0D4] hover:border-[#E07B39] transition-colors"
              data-testid="admin-refresh-btn"
            >
              ↻ refresh
            </button>
            <button
              onClick={logout}
              className="text-[13px] font-mono text-[#8B7355] hover:text-[#1A1A2E] px-3 py-2 rounded-full border border-[#EDE0D4] hover:border-[#E07B39] transition-colors"
              data-testid="admin-logout-btn"
            >
              sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="Last 7 days" value={stats.last_week} accent />
            <StatCard label="Pending" value={stats.pending} />
            <StatCard label="Contacted" value={stats.contacted} />
            <StatCard
              label="Feedback"
              value={`${stats.feedback_unread || 0}/${stats.feedback_total || 0}`}
              accent={(stats.feedback_unread || 0) > 0}
              sublabel={(stats.feedback_unread || 0) > 0 ? "unread" : "all read"}
            />
          </div>
        )}

        {stats && Object.keys(stats.by_language || {}).length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="admin-lang-breakdown">
            {Object.entries(stats.by_language)
              .sort((a, b) => b[1] - a[1])
              .map(([lang, n]) => (
                <span key={lang} className="inline-flex items-center gap-2 bg-white border border-[#EDE0D4] rounded-full px-3 py-1.5 text-[12px] font-mono text-[#8B7355]">
                  <span className="text-[#1A1A2E] font-medium">{lang}</span>
                  <span className="text-[#E07B39]">{n}</span>
                </span>
              ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#EDE0D4] mb-6">
          <button
            onClick={() => setTab("followups")}
            className={`px-4 py-3 text-[14px] font-medium transition-colors border-b-2 -mb-px ${
              tab === "followups"
                ? "border-[#E07B39] text-[#1A1A2E]"
                : "border-transparent text-[#8B7355] hover:text-[#1A1A2E]"
            }`}
            data-testid="tab-followups"
          >
            WhatsApp follow-ups <span className="font-mono text-[11px] ms-1 text-[#8B7355]">{rows.length}</span>
          </button>
          <button
            onClick={() => setTab("feedback")}
            className={`px-4 py-3 text-[14px] font-medium transition-colors border-b-2 -mb-px ${
              tab === "feedback"
                ? "border-[#E07B39] text-[#1A1A2E]"
                : "border-transparent text-[#8B7355] hover:text-[#1A1A2E]"
            }`}
            data-testid="tab-feedback"
          >
            💬 Feedback & requests <span className="font-mono text-[11px] ms-1 text-[#E07B39]">{stats?.feedback_unread || 0}</span>
          </button>
          <button
            onClick={() => setTab("orgs")}
            className={`px-4 py-3 text-[14px] font-medium transition-colors border-b-2 -mb-px ${
              tab === "orgs"
                ? "border-[#E07B39] text-[#1A1A2E]"
                : "border-transparent text-[#8B7355] hover:text-[#1A1A2E]"
            }`}
            data-testid="tab-orgs"
          >
            Organizations
          </button>
        </div>

        {tab === "orgs" ? (
          <OrgsPanel token={token} />
        ) : tab === "followups" ? (
          <>
            {/* Filter */}
            <div className="flex items-center gap-2 mb-4">
          {[
            { k: "all", label: "All" },
            { k: "pending", label: "Pending" },
            { k: "contacted", label: "Contacted" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all min-h-[40px] ${
                filter === f.k
                  ? "bg-[#1A1A2E] text-white"
                  : "bg-white text-[#8B7355] border border-[#EDE0D4] hover:border-[#E07B39] hover:text-[#1A1A2E]"
              }`}
              data-testid={`filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
          <span className="ms-auto font-mono text-[12px] text-[#8B7355]">
            {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
          </span>
        </div>

        {/* Table */}
        {loading && rows.length === 0 ? (
          <div className="py-20 text-center font-mono text-[13px] text-[#8B7355]">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center" data-testid="admin-empty">
            <div className="text-4xl mb-3">🌸</div>
            <div className="font-heading text-[22px] text-[#1A1A2E] mb-1">No follow-ups yet.</div>
            <div className="text-[13px] text-[#8B7355]">They'll show up here the moment someone asks to talk to a person.</div>
          </div>
        ) : (
          <div className="bg-white border border-[#EDE0D4] rounded-3xl overflow-hidden shadow-[0_2px_10px_rgba(26,26,46,0.04)]">
            <div className="hidden md:grid grid-cols-[1.5fr_1.2fr_1fr_0.8fr_0.9fr_0.7fr] gap-4 px-6 py-3 border-b border-[#EDE0D4] bg-[#FFFBF5] font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355]">
              <div>Name</div>
              <div>WhatsApp</div>
              <div>Best time</div>
              <div>Language</div>
              <div>When</div>
              <div>Status</div>
            </div>
            <ul className="divide-y divide-[#EDE0D4]">
              {filtered.map((r) => (
                <li key={r.id} data-testid={`followup-row-${r.id}`}>
                  <div className="md:grid md:grid-cols-[1.5fr_1.2fr_1fr_0.8fr_0.9fr_0.7fr] gap-4 px-5 md:px-6 py-4 items-center hover:bg-[#FFFBF5] transition-colors">
                    <button
                      onClick={() => openConv(r)}
                      className="text-start"
                      data-testid="open-conv-btn"
                    >
                      <div className="font-heading text-[19px] leading-tight text-[#1A1A2E] mb-0.5">{r.name || "—"}</div>
                      <div className="md:hidden font-mono text-[11px] text-[#8B7355]">{formatDate(r.created_at)} · {r.language_name}</div>
                    </button>
                    <a
                      href={waLink(r.whatsapp)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-[14px] font-mono text-[#0B7EA3] hover:underline w-fit"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.whatsapp}
                      <span className="text-[10px]">↗</span>
                    </a>
                    <div className="text-[13px] text-[#1A1A2E]">{r.time}</div>
                    <div className="hidden md:block">
                      <span className="inline-block bg-[#FFF3E0] text-[#1A1A2E] text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#EDE0D4]">
                        {r.language_name}
                      </span>
                    </div>
                    <div className="hidden md:block font-mono text-[11px] text-[#8B7355]">{formatDate(r.created_at)}</div>
                    <div>
                      <button
                        onClick={() => toggleContacted(r)}
                        className={`text-[12px] font-medium px-3 py-1.5 rounded-full min-h-[32px] transition-all ${
                          r.contacted
                            ? "bg-[#388E3C]/10 text-[#388E3C] border border-[#388E3C]/30 hover:bg-[#388E3C]/20"
                            : "bg-[#E07B39]/10 text-[#E07B39] border border-[#E07B39]/30 hover:bg-[#E07B39]/20"
                        }`}
                        data-testid={`toggle-contacted-${r.id}`}
                      >
                        {r.contacted ? "✓ contacted" : "pending"}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-10 text-center">
          <a href="/" className="font-mono text-[12px] text-[#8B7355] hover:text-[#1A1A2E] transition-colors">
            ← back to saathi chat
          </a>
        </div>
          </>
        ) : (
          <FeedbackList
            items={feedback}
            onOpen={openConv}
            onToggleRead={markFeedbackRead}
          />
        )}
      </div>

      {/* Conversation drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex" data-testid="conv-drawer">
          <div className="flex-1 bg-[#1A1A2E]/30 backdrop-blur-sm" onClick={closeDrawer} />
          <div className="w-full max-w-[540px] bg-[#FFFBF5] border-s border-[#EDE0D4] h-full flex flex-col shadow-2xl">
            <div className="sticky top-0 bg-[#FFFBF5]/95 backdrop-blur-xl border-b border-[#EDE0D4] px-6 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#E07B39] mb-1">conversation</div>
                <div className="font-heading text-[22px] leading-tight text-[#1A1A2E] truncate">{selected.name || "—"}</div>
                <div className="font-mono text-[11px] text-[#8B7355] mt-0.5">{selected.language_name} · {formatDate(selected.created_at)}</div>
              </div>
              <button onClick={closeDrawer} className="w-9 h-9 rounded-full bg-white border border-[#EDE0D4] text-[#8B7355] hover:text-[#1A1A2E] hover:border-[#E07B39] transition-all" data-testid="close-drawer-btn">×</button>
            </div>

            <div className="px-6 py-4 border-b border-[#EDE0D4] bg-white">
              <div className="flex flex-wrap gap-2 mb-3">
                <a href={waLink(selected.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#128C7E] border border-[#25D366]/30 rounded-full px-4 py-2 text-[13px] font-medium hover:bg-[#25D366]/20 transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4l-2.7-1.3c-.3-.2-.7-.1-.9.2l-.8 1c-1.5-.8-2.7-2-3.5-3.5l1-.8c.3-.2.4-.6.2-.9L9.6 6.5c-.2-.4-.6-.5-1-.3l-2 1c-.4.2-.6.6-.5 1 .7 4.7 4.4 8.4 9.1 9.1.4.1.8-.1 1-.5l1-2c.2-.4.1-.8-.3-1z"/></svg>
                  WhatsApp {selected.whatsapp}
                </a>
                <span className="inline-flex items-center bg-white border border-[#EDE0D4] text-[#1A1A2E] rounded-full px-3 py-2 text-[12px]">
                  ⏰ {selected.time}
                </span>
              </div>
              <NotesEditor row={selected} onSave={saveNotes} />
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {convLoading ? (
                <div className="py-10 text-center font-mono text-[12px] text-[#8B7355]">loading…</div>
              ) : conv?.error ? (
                <div className="py-10 text-center text-[13px] text-[#D32F2F]">{conv.error}</div>
              ) : !conv?.messages?.length ? (
                <div className="py-10 text-center">
                  <div className="text-3xl mb-2">💬</div>
                  <div className="text-[13px] text-[#8B7355]">No transcript stored for this follow-up.</div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {conv.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      {m.role === "assistant" && (
                        <div className="w-7 h-7 rounded-full bg-[#FFF3E0] border border-[#EDE0D4] flex items-center justify-center text-sm me-2 shrink-0 mt-1">🌸</div>
                      )}
                      <div className={`max-w-[85%] px-4 py-2.5 text-[14px] leading-[1.6] whitespace-pre-wrap relative ${
                        m.role === "user"
                          ? (m.is_feedback
                              ? "bg-[#E07B39] text-white rounded-2xl rounded-ee-sm ring-2 ring-[#E07B39]/30"
                              : "bg-[#0B7EA3] text-white rounded-2xl rounded-ee-sm")
                          : "bg-white text-[#1A1A2E] border border-[#EDE0D4] rounded-2xl rounded-ss-sm"
                      }`}>
                        {m.is_feedback && (
                          <span className="absolute -top-2 end-2 bg-[#E07B39] text-white text-[9px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full border-2 border-[#FFFBF5]">
                            💬 feedback
                          </span>
                        )}
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, sublabel }) {
  return (
    <div className={`rounded-2xl border border-[#EDE0D4] px-5 py-4 ${accent ? "bg-[#FFF3E0]" : "bg-white"}`}>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-1">{label}</div>
      <div className="font-heading text-[36px] leading-none text-[#1A1A2E]">{value}</div>
      {sublabel && <div className="font-mono text-[10px] text-[#E07B39] mt-1">{sublabel}</div>}
    </div>
  );
}

function FeedbackList({ items, onOpen, onToggleRead }) {
  if (!items.length) {
    return (
      <div className="py-20 text-center" data-testid="feedback-empty">
        <div className="text-4xl mb-3">💬</div>
        <div className="font-heading text-[22px] text-[#1A1A2E] mb-1">No feedback yet.</div>
        <div className="text-[13px] text-[#8B7355] max-w-sm mx-auto">
          Saathi invites feedback in conversation — anything users say about confusion, requests, or praise will show up here.
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3" data-testid="feedback-list">
      {items.map((f) => (
        <button
          key={f.conversation_id}
          onClick={() => onOpen({ conversation_id: f.conversation_id, name: "Anonymous", language_name: f.language_name, created_at: f.created_at, whatsapp: "", time: "" })}
          className={`text-start bg-white border rounded-2xl px-5 py-4 hover:border-[#E07B39] transition-all shadow-[0_2px_10px_rgba(26,26,46,0.03)] ${
            f.read ? "border-[#EDE0D4] opacity-75" : "border-[#E07B39]/40 ring-1 ring-[#E07B39]/10"
          }`}
          data-testid={`feedback-row-${f.conversation_id}`}
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center bg-[#FFF3E0] text-[#1A1A2E] text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#EDE0D4]">
                {f.language_name}
              </span>
              <span className="font-mono text-[11px] text-[#8B7355]">
                {f.feedback_count} flagged · {f.message_count} msgs total
              </span>
              {!f.read && <span className="inline-block w-2 h-2 rounded-full bg-[#E07B39]" />}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-[11px] text-[#8B7355]">{new Date(f.last_feedback_at || f.updated_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onToggleRead(f.conversation_id, !f.read); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onToggleRead(f.conversation_id, !f.read); }}}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-all cursor-pointer ${
                  f.read
                    ? "bg-[#8B7355]/10 text-[#8B7355] border border-[#8B7355]/30 hover:bg-[#8B7355]/20"
                    : "bg-[#E07B39]/10 text-[#E07B39] border border-[#E07B39]/30 hover:bg-[#E07B39]/20"
                }`}
                data-testid={`toggle-read-${f.conversation_id}`}
              >
                {f.read ? "read" : "mark read"}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {f.feedback_snippets.slice(0, 2).map((s, i) => (
              <div key={i} className="text-[14px] leading-[1.55] text-[#1A1A2E] bg-[#FFFBF5] border-s-2 border-[#E07B39] ps-3 py-1.5 rounded-e-lg">
                {s.content}
              </div>
            ))}
            {f.feedback_count > 2 && (
              <div className="font-mono text-[11px] text-[#8B7355]">+ {f.feedback_count - 2} more…</div>
            )}
          </div>
          <div className="mt-3 font-mono text-[11px] text-[#E07B39]">open transcript →</div>
        </button>
      ))}
    </div>
  );
}

function NotesEditor({ row, onSave }) {
  const [val, setVal] = useState(row.notes || "");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(row.notes || ""); setDirty(false); }, [row.id]); // eslint-disable-line
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B7355] mb-2">internal notes</div>
      <textarea
        value={val}
        onChange={(e) => { setVal(e.target.value); setDirty(e.target.value !== (row.notes || "")); }}
        onBlur={() => { if (dirty) { onSave(row, val); setDirty(false); } }}
        placeholder="Notes only your team sees…"
        className="w-full bg-[#FFFBF5] border border-[#EDE0D4] rounded-xl px-3 py-2 text-[13px] text-[#1A1A2E] placeholder:text-[#8B7355]/70 focus:outline-none focus:border-[#E07B39] focus:ring-2 focus:ring-[#E07B39]/20 resize-none font-body"
        rows={2}
        data-testid="notes-textarea"
      />
      {dirty && <div className="font-mono text-[10px] text-[#E07B39] mt-1">unsaved — click outside to save</div>}
    </div>
  );
}
